import api from '@/api';
import DashboardSearchRequest from '@/model/Dashboard/DashboardSearchRequest';
import AffiliateDashboardSearchRequest from '@/model/Dashboard/AffiliateDashboardSearchRequest';
import AffiliateDashboard2SearchRequest from '@/model/Dashboard/AffiliateDashboard2SearchRequest';
import MonetaryDashboardSearchRequest from '@/model/Dashboard/MonetaryDashboardSearchRequest';
import ClientDashboardSearchRequest from '@/model/Dashboard/ClientDashboardSearchRequest';
import ClientSearchRequest from '@/model/client/ClientSearchRequest';
import SalesDrillRequest from '@/model/Dashboard/SalesDrillRequest';

export default {
  namespaced: true,
  state: {},
  getters: {},
  mutations: {},
  actions: {
    getSalesDashboard(actions, filter) {
      return api.base()
        .post('/dashboard/sales', DashboardSearchRequest(filter))
        .then(
          response => {
            return response.data.data;
          },
          err => Promise.reject(err)
        );
    },
    // Salesman drill: downstream money others made on the clients he FTD'd.
    getSalesmanBreakdown(actions, payload) {
      const p = payload || {};
      return api.base()
        .post('/dashboard/sales/salesman', {
          salesmanId: p.salesmanId,
          range: { from: p.range?.from || null, until: p.range?.until || null },
          orgIds: p.orgIds || [],
          buIds: p.buIds || [],
          buTypes: p.buTypes || [],
          realOnly: p.realOnly == null ? null : p.realOnly,
          pagination: p.pagination || { page: 1, size: 25 },
          ftdPagination: p.ftdPagination || { page: 1, size: 25 },
          fakeFtdPagination: p.fakeFtdPagination || { page: 1, size: 25 },
        })
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    // Sales Dashboard drill-down: deposits or clients behind a clicked chart segment.
    getSalesDrillList(actions, payload) {
      return api.base()
        .post('/dashboard/sales/list', SalesDrillRequest(payload))
        .then(
          response => ({
            data: response.data.data,
            metadata: response.data.metadata || {},
          }),
          err => Promise.reject(err)
        );
    },
    // Merged Sales + Retention Excel report (backend-built styled XLSX).
    downloadSalesReport(actions, payload) {
      const p = payload || {};
      const request = {
        range: { from: p.range?.from || null, until: p.range?.until || null },
        orgIds: p.orgIds || [],
        buIds: p.buIds || [],
        buTypes: p.buTypes || [],
        userIds: p.userIds || [],
        realOnly: p.realOnly == null ? null : p.realOnly,
      };
      return api.base()
        .post('/dashboard/sales/report/download', request, { responseType: 'blob' })
        .then(response => {
          const url = URL.createObjectURL(new Blob([response.data],
            { type: response.headers['content-type'] }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', response.headers['file-name'] || 'sales_report.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          return true;
        });
    },
    // Assignment feed behind the "leads received" numbers: who received which
    // lead/client, from whom and how (kind=SALES|RETENTION, omitted = both).
    getAssignments(actions, { filter, kind }) {
      const p = filter || {};
      const request = {
        range: { from: p.range?.from || null, until: p.range?.until || null },
        orgIds: p.orgIds || [],
        buIds: p.buIds || [],
        userIds: p.userIds || [],
        page: p.page || 1,
        size: p.size || 25,
      };
      const query = kind ? `?kind=${encodeURIComponent(kind)}` : '';
      return api.base()
        .post(`/dashboard/assignments/list${query}`, request)
        .then(
          response => ({
            data: response.data.data || [],
            pagination: response.data.metadata || {},
          }),
          err => Promise.reject(err)
        );
    },
    getMonetaryDashboard(actions, filter) {
      return api.base()
        .post('/dashboard/monetary', MonetaryDashboardSearchRequest(filter))
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    getFinanceDashboard(actions, filter) {
      return api.base()
        .post('/dashboard/finance', MonetaryDashboardSearchRequest(filter))
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    getClientInsightsTop(actions, filter) {
      const p = filter || {};
      const request = {
        ...MonetaryDashboardSearchRequest(p),
        search: p.search || null,
        page: p.page || 1,
        size: p.size || 20,
        bonusMode: p.bonusMode || null,
        fakeMode: p.fakeMode || null,
        txfMode: p.txfMode || null,
        countries: p.countries || [],
        affiliateIds: p.affiliateIds || [],
        statusIds: p.statusIds || [],
        revealToken: p.revealToken || null,
      };
      return api.base()
        .post('/dashboard/client-insights/top', request)
        .then(
          response => ({ data: response.data.data, pagination: response.data.metadata }),
          err => Promise.reject(err)
        );
    },
    getClientInsight(actions, payload) {
      // Accepts a bare clientId (lifetime) or { clientId, range: {from, until} }
      const p = typeof payload === 'string' ? { clientId: payload } : (payload || {});
      return api.base()
        .post(`/dashboard/client-insights/client/${p.clientId}`, { range: p.range || null })
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    getUserInsightsTop(actions, filter) {
      const p = filter || {};
      const request = {
        ...MonetaryDashboardSearchRequest(p),
        search: p.search || null,
        page: p.page || 1,
        size: p.size || 20,
        revealToken: p.revealToken || null,
      };
      return api.base()
        .post('/dashboard/user-insights/top', request)
        .then(
          response => ({ data: response.data.data, pagination: response.data.metadata }),
          err => Promise.reject(err)
        );
    },
    getUserInsight(actions, payload) {
      // Accepts a bare userId (lifetime) or { userId, range: {from, until} }
      // so the modal can honor the dashboard's date filter.
      const p = typeof payload === 'string' ? { userId: payload } : (payload || {});
      return api.base()
        .post(`/dashboard/user-insights/user/${p.userId}`, { range: p.range || null })
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    downloadInsightsExcel(actions, { kind, filter }) {
      const p = filter || {};
      const request = {
        ...MonetaryDashboardSearchRequest(p),
        search: p.search || null,
        bonusMode: p.bonusMode || null,
        fakeMode: p.fakeMode || null,
        txfMode: p.txfMode || null,
        countries: p.countries || [],
        affiliateIds: p.affiliateIds || [],
        statusIds: p.statusIds || [],
      };
      const path = kind === 'users' ? '/dashboard/user-insights/export' : '/dashboard/client-insights/export';
      return api.base()
        .post(path, request, { responseType: 'blob' })
        .then(response => {
          const url = URL.createObjectURL(new Blob([response.data],
            { type: response.headers['content-type'] }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', response.headers['file-name'] || `${kind}_insights.xlsx`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          return true;
        });
    },
    getClientDashboard(actions, filter) {
      return api.base()
        .post('/dashboard/client', ClientDashboardSearchRequest(filter))
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    // Drill-down: the actual clients behind a single dashboard cell.
    getClientDashboardClients(actions, payload) {
      return api.base()
        .post('/dashboard/client/list', payload)
        .then(
          response => ({
            data: response.data.data,
            metadata: response.data.metadata || {},
          }),
          err => Promise.reject(err)
        );
    },
    // AffiliateDashboard2: rows come pre-grouped (affiliate/desk/org/bu) with
    // totals + the "Duplicated" (no affiliate) bucket built server-side.
    getAffiliateDashboard2(actions, filter) {
      return api.base()
        .post('/dashboard/affiliate2', AffiliateDashboard2SearchRequest(filter))
        .then(
          response => response.data.data,
          err => Promise.reject(err)
        );
    },
    // AffiliateDashboard2 styled multi-sheet Excel export (backend-built XLSX).
    downloadAffiliateDashboard2(actions, filter) {
      return api.base()
        .post('/dashboard/affiliate2/export', AffiliateDashboard2SearchRequest(filter), { responseType: 'blob' })
        .then(response => {
          const url = URL.createObjectURL(new Blob([response.data],
            { type: response.headers['content-type'] }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', response.headers['file-name'] || 'affiliate_dashboard2.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          return true;
        });
    },
    getAffiliateDashboard(actions, { pagination, sort, filter }) {
      return api.base()
        .post('/dashboard/affiliate',  AffiliateDashboardSearchRequest({
            ...(filter || {}),
            pagination,
            sort,
          })
        )
        .then(
          response => ({
            data: response.data.data,
            pagination: {
              page: response.data.metadata.page,
              pages: response.data.metadata.pages,
              total: response.data.metadata.total
            },
            sort: response.data.metadata.sort
          })
        );
    },

  }
};
